/**
 * Admin SAP Health tile — shows the last 24h of `sap_call_metrics`
 * aggregated per endpoint. Powered by:
 *   • GET /api/admin/sap/metrics/summary?hours=24
 *   • GET /api/admin/sap/metrics/recent?limit=50
 *
 * Two views: `summary` (per-endpoint p50/p95/count/error-rate) and
 * `recent` (last N raw calls, newest first). Admin-only.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Home, Loader2, Activity, RefreshCcw, AlertTriangle,
  CheckCircle2, Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMs = (ms) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const fmtBytes = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)}KB`;
  return `${(b / 1024 / 1024).toFixed(1)}MB`;
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
};

const statusPill = (code) => {
  if (code == null) return "bg-red-100 text-red-800 border-red-200";
  if (code >= 500)  return "bg-red-100 text-red-800 border-red-200";
  if (code >= 400)  return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-emerald-100 text-emerald-800 border-emerald-200";
};

export default function SapHealth() {
  const nav = useNavigate();
  const [hours, setHours] = useState(24);
  const [tab, setTab] = useState("summary"); // summary | recent
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "summary") {
        const r = await axios.get(`${API}/admin/sap/metrics/summary?hours=${hours}`,
                                    { headers: authHeaders() });
        setSummary(r.data);
      } else {
        const r = await axios.get(`${API}/admin/sap/metrics/recent?limit=100`,
                                    { headers: authHeaders() });
        setRecent(r.data?.rows || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load SAP metrics");
    } finally {
      setLoading(false);
    }
  }, [tab, hours]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/sap")}
                    className="text-white hover:bg-white/10"
                    data-testid="health-back-sap">
              <ArrowLeft size={16} className="mr-1" /> SAP Workspace
            </Button>
            <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                    className="text-white hover:bg-white/10"
                    data-testid="health-back-home">
              <Home size={16} className="mr-1" /> Home
            </Button>
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <div className="flex items-center gap-2">
              <Activity size={18} />
              <div className="text-sm sm:text-base font-bold tracking-tight">SAP Health · Call Metrics</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))}
                    data-testid="health-window-select"
                    className="h-8 bg-white/10 border border-white/20 rounded-sm px-2 text-xs">
              <option value={1}>Last 1h</option>
              <option value={6}>Last 6h</option>
              <option value={24}>Last 24h</option>
              <option value={168}>Last 7d</option>
            </select>
            <Button size="sm" onClick={load} disabled={loading}
                    data-testid="health-refresh-btn"
                    className="bg-white/10 border border-white/20 hover:bg-white/20">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-4">
        {/* Tabs */}
        <div className="inline-flex bg-slate-100 rounded-sm p-1">
          <button data-testid="tab-summary"
                  onClick={() => setTab("summary")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm inline-flex items-center gap-1 ${
                    tab === "summary" ? "bg-white text-cyan-800 shadow-sm" : "text-slate-600"
                  }`}>
            <Zap size={12} /> Per-endpoint
          </button>
          <button data-testid="tab-recent"
                  onClick={() => setTab("recent")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm inline-flex items-center gap-1 ${
                    tab === "recent" ? "bg-white text-cyan-800 shadow-sm" : "text-slate-600"
                  }`}>
            <Activity size={12} /> Recent calls
          </button>
        </div>

        {/* Summary panel */}
        {tab === "summary" && (
          <>
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Total SAP calls"     value={summary.total_calls?.toLocaleString() ?? "—"}
                          testid="stat-total-calls" />
                <StatCard label="Avg latency"         value={fmtMs(summary.avg_ms)}
                          testid="stat-avg-ms" />
                <StatCard label="Error rate"          value={`${(summary.error_rate * 100).toFixed(1)}%`}
                          testid="stat-error-rate"
                          alert={summary.error_rate > 0.05} />
                <StatCard label="Window"              value={`${summary.hours}h`}
                          testid="stat-window" />
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 text-xs font-semibold text-slate-700">
                Per-endpoint (sorted by total time)
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="summary-table">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-slate-500">
                      <th className="px-3 py-2">Endpoint</th>
                      <th className="px-3 py-2 text-right">Calls</th>
                      <th className="px-3 py-2 text-right">Avg</th>
                      <th className="px-3 py-2 text-right">p50</th>
                      <th className="px-3 py-2 text-right">p95</th>
                      <th className="px-3 py-2 text-right">p99</th>
                      <th className="px-3 py-2 text-right">Errors</th>
                      <th className="px-3 py-2 text-right">Bytes</th>
                      <th className="px-3 py-2 text-right">Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary?.endpoints || []).map((r) => (
                      <tr key={r.endpoint} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="px-3 py-2 mono text-slate-700 max-w-[420px] truncate" title={r.endpoint}>
                          {r.endpoint}
                        </td>
                        <td className="px-3 py-2 text-right num font-semibold">{r.count}</td>
                        <td className="px-3 py-2 text-right num">{fmtMs(r.avg_ms)}</td>
                        <td className="px-3 py-2 text-right num">{fmtMs(r.p50_ms)}</td>
                        <td className={`px-3 py-2 text-right num ${r.p95_ms > 5000 ? "text-red-700 font-semibold" : ""}`}>
                          {fmtMs(r.p95_ms)}
                        </td>
                        <td className="px-3 py-2 text-right num">{fmtMs(r.p99_ms)}</td>
                        <td className={`px-3 py-2 text-right num ${r.errors > 0 ? "text-red-700 font-semibold" : "text-slate-400"}`}>
                          {r.errors > 0 ? `${r.errors} (${(r.error_rate * 100).toFixed(1)}%)` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right num text-slate-500">{fmtBytes(r.total_bytes)}</td>
                        <td className="px-3 py-2 text-right num text-slate-500">{fmtRelative(r.last_at)}</td>
                      </tr>
                    ))}
                    {(!summary || !summary.endpoints?.length) && !loading && (
                      <tr>
                        <td colSpan={9} className="px-3 py-8 text-center text-slate-500 text-xs">
                          No SAP calls recorded in the selected window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Recent panel */}
        {tab === "recent" && (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 text-xs font-semibold text-slate-700">
              Last {recent.length} SAP calls (newest first)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="recent-table">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="px-3 py-2">Started</th>
                    <th className="px-3 py-2">Method</th>
                    <th className="px-3 py-2">Endpoint</th>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2 text-right">Status</th>
                    <th className="px-3 py-2 text-right">Elapsed</th>
                    <th className="px-3 py-2 text-right">Bytes</th>
                    <th className="px-3 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-3 py-2 mono text-slate-500 text-fiori-caption">
                        {fmtRelative(r.started_at)}
                      </td>
                      <td className="px-3 py-2 font-semibold text-slate-700">{r.method}</td>
                      <td className="px-3 py-2 mono text-slate-700 max-w-[400px] truncate" title={r.url}>
                        {r.endpoint}
                      </td>
                      <td className="px-3 py-2 mono text-fiori-caption text-slate-600">{r.user}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-flex px-1.5 py-0.5 text-fiori-caption font-semibold rounded-sm border ${statusPill(r.status)}`}>
                          {r.status ?? "ERR"}
                        </span>
                      </td>
                      <td className={`px-3 py-2 text-right num ${r.elapsed_ms > 5000 ? "text-red-700 font-semibold" : ""}`}>
                        {fmtMs(r.elapsed_ms)}
                      </td>
                      <td className="px-3 py-2 text-right num text-slate-500">{fmtBytes(r.response_bytes)}</td>
                      <td className="px-3 py-2 text-red-700 text-fiori-caption max-w-[200px] truncate">{r.error || ""}</td>
                    </tr>
                  ))}
                  {!recent.length && !loading && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-slate-500 text-xs">
                        No recent SAP calls.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, testid, alert }) {
  return (
    <div className={`bg-white border rounded-sm p-3 ${
      alert ? "border-red-300 bg-red-50" : "border-slate-200"
    }`} data-testid={testid}>
      <div className="flex items-center gap-1 text-fiori-caption text-slate-500 uppercase tracking-wide">
        {alert ? <AlertTriangle size={11} className="text-red-600" />
               : <CheckCircle2 size={11} className="text-emerald-600" />}
        {label}
      </div>
      <div className={`text-lg font-bold mt-1 num ${alert ? "text-red-800" : "text-slate-800"}`}>
        {value}
      </div>
    </div>
  );
}
